import React, { useState, useEffect, useMemo } from 'react';
import { C } from '../utils/theme.js';
import Card from './Card.jsx';
import { DXCC_LIST } from '../data/dxcc.js';
import { PREFIX_ALIAS } from '../data/prefixAlias.js';

function tr(lang, pt, en) { return lang === "pt" ? pt : en; }

var DIAS_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];
var DIAS_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
var BAND_ORDER = ["160m", "80m", "40m", "30m", "20m", "17m", "15m", "12m", "10m", "6m", "2m", "70cm"];
var CONTINENTES = ["AF", "AN", "AS", "EU", "NA", "OC", "SA"];

// Media so vira recomendacao com amostra suficiente; abaixo disso um unico QSO
// exotico faria um horario aleatorio parecer o melhor da estacao.
var MIN_AMOSTRA = 5;

// Entidade -> todos os prefixos conhecidos. O prefixo principal vem da tabela
// DXCC e os demais sao os aliases que apontam para ele (PY <- PP, PQ, PU, ZZ...).
function construirPrefixos() {
  var porPx = {};
  for (var i = 0; i < DXCC_LIST.length; i++) {
    var d = DXCC_LIST[i];
    if (!porPx[d.entity]) porPx[d.entity] = {};
    porPx[d.entity][d.px] = 1;
  }
  var pxParaEntidade = {};
  for (var j = 0; j < DXCC_LIST.length; j++) pxParaEntidade[DXCC_LIST[j].px] = DXCC_LIST[j].entity;
  for (var alias in PREFIX_ALIAS) {
    var ent = pxParaEntidade[PREFIX_ALIAS[alias]];
    if (ent) porPx[ent][alias] = 1;
  }
  var saida = {};
  Object.keys(porPx).forEach(function(e) { saida[e] = Object.keys(porPx[e]).sort(); });
  return saida;
}

// Barra horizontal com rotulo, valor e contagem da amostra.
function Barra(props) {
  var pct = props.max > 0 ? Math.round(props.valor / props.max * 100) : 0;
  var destaque = props.destaque;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
      <div style={{ width: 46, fontSize: 11, color: destaque ? C.a2 : C.t3, fontFamily: "'Courier New',monospace", textAlign: "right", fontWeight: destaque ? 700 : 400 }}>{props.rotulo}</div>
      <div style={{ flex: 1, height: 14, background: C.s1, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: pct + "%", height: "100%", background: destaque ? C.a2 : C.a1, opacity: destaque ? 0.9 : 0.55, transition: "width .3s" }} />
      </div>
      <div style={{ width: 74, fontSize: 10, color: C.t3, fontFamily: "'Courier New',monospace" }}>
        {props.texto}
      </div>
    </div>
  );
}

function Secao(props) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, color: C.a1, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>{props.titulo}</div>
      {props.children}
    </div>
  );
}

export default function DXEffectiveness(props) {
  var lang = props.lang;
  var qsos = props.qsos || [];
  var dd = props.dd || {};
  var dias = lang === "pt" ? DIAS_PT : DIAS_EN;

  var rarSt = useState(null); var rar = rarSt[0]; var setRar = rarSt[1];
  var novoTipoSt = useState("entidade"); var novoTipo = novoTipoSt[0]; var setNovoTipo = novoTipoSt[1];
  var expModoSt = useState("faltantes"); var expModo = expModoSt[0]; var setExpModo = expModoSt[1];
  var expContSt = useState("EU"); var expCont = expContSt[0]; var setExpCont = expContSt[1];
  var expBandaSt = useState("20m"); var expBanda = expBandaSt[0]; var setExpBanda = expBandaSt[1];
  var expSepSt = useState("virgula"); var expSep = expSepSt[0]; var setExpSep = expSepSt[1];
  var expWildSt = useState(false); var expWild = expWildSt[0]; var setExpWild = expWildSt[1];
  var copiadoSt = useState(false); var copiado = copiadoSt[0]; var setCopiado = copiadoSt[1];

  useEffect(function() {
    var vivo = true;
    fetch("/api/dxcc/rarity")
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { if (vivo && d && d.ready) setRar(d); })
      .catch(function(e) { console.error("[HamExpert] falha ao carregar a raridade DXCC:", e); });
    return function() { vivo = false; };
  }, []);

  var PREFIXOS = useMemo(construirPrefixos, []);

  // Indice 0-100: 100 = so um punhado de operadores da base alcancou a entidade.
  function indiceRaridade(entidade) {
    if (!rar || !rar.totalOps) return null;
    var ops = rar.entities[entidade];
    if (!ops) return null;
    return Math.round((1 - ops / rar.totalOps) * 100);
  }

  function diaDaSemana(data) {
    if (!data || data.length < 8) return null;
    var d = new Date(Date.UTC(+data.substring(0, 4), +data.substring(4, 6) - 1, +data.substring(6, 8)));
    return isNaN(d.getTime()) ? null : d.getUTCDay();
  }

  // ── 1. Efetividade: onde os contatos sao mais raros ──────────
  var efetividade = useMemo(function() {
    var vazio = { horas: [], dias: [], bandas: [], comRaridade: 0 };
    if (!rar) return vazio;
    var horas = [], diasAc = [], bandas = {};
    for (var h = 0; h < 24; h++) horas.push({ chave: h, soma: 0, n: 0 });
    for (var d = 0; d < 7; d++) diasAc.push({ chave: d, soma: 0, n: 0 });
    var comRaridade = 0;
    for (var i = 0; i < qsos.length; i++) {
      var q = qsos[i];
      var idx = indiceRaridade(q.entity);
      if (idx === null) continue;
      comRaridade++;
      var hh = q.time && q.time.length >= 2 ? parseInt(q.time.substring(0, 2), 10) : null;
      if (hh !== null && hh >= 0 && hh < 24) { horas[hh].soma += idx; horas[hh].n++; }
      var dw = diaDaSemana(q.date);
      if (dw !== null) { diasAc[dw].soma += idx; diasAc[dw].n++; }
      var b = q.band || "";
      if (b) { if (!bandas[b]) bandas[b] = { chave: b, soma: 0, n: 0 }; bandas[b].soma += idx; bandas[b].n++; }
    }
    function media(lista) {
      return lista.map(function(x) { return { chave: x.chave, media: x.n ? x.soma / x.n : 0, n: x.n }; });
    }
    var listaBandas = media(Object.keys(bandas).map(function(b) { return bandas[b]; }));
    listaBandas.sort(function(a, b) {
      var ia = BAND_ORDER.indexOf(a.chave), ib = BAND_ORDER.indexOf(b.chave);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    return { horas: media(horas), dias: media(diasAc), bandas: listaBandas, comRaridade: comRaridade };
  }, [qsos, rar]);

  // ── 2. Quando entram entidades/grids ineditos ────────────────
  var novidades = useMemo(function() {
    var horas = [], diasAc = [], bandas = {};
    for (var h = 0; h < 24; h++) horas.push({ chave: h, n: 0 });
    for (var d = 0; d < 7; d++) diasAc.push({ chave: d, n: 0 });
    // Ordem cronologica: "primeira vez" so faz sentido percorrendo o log em ordem.
    var ordenados = qsos.slice().sort(function(a, b) {
      var ka = (a.date || "") + (a.time || ""), kb = (b.date || "") + (b.time || "");
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    });
    var vistos = {};
    var total = 0;
    for (var i = 0; i < ordenados.length; i++) {
      var q = ordenados[i];
      var chave = novoTipo === "entidade"
        ? (q.entity && q.entity !== "Unknown" ? q.entity : null)
        : (q.grid ? q.grid.substring(0, 4).toUpperCase() : null);
      if (!chave || vistos[chave]) continue;
      vistos[chave] = 1;
      total++;
      var hh = q.time && q.time.length >= 2 ? parseInt(q.time.substring(0, 2), 10) : null;
      if (hh !== null && hh >= 0 && hh < 24) horas[hh].n++;
      var dw = diaDaSemana(q.date);
      if (dw !== null) diasAc[dw].n++;
      var b = q.band || "";
      if (b) bandas[b] = (bandas[b] || 0) + 1;
    }
    var listaBandas = Object.keys(bandas).map(function(b) { return { chave: b, n: bandas[b] }; });
    listaBandas.sort(function(a, b) {
      var ia = BAND_ORDER.indexOf(a.chave), ib = BAND_ORDER.indexOf(b.chave);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    return { horas: horas, dias: diasAc, bandas: listaBandas, total: total };
  }, [qsos, novoTipo]);

  // ── 3. Exportacao de prefixos do que ainda falta ─────────────
  var entidadesExport = useMemo(function() {
    return DXCC_LIST.filter(function(d) {
      var info = dd[d.entity];
      if (expModo === "faltantes") return !info;
      if (expModo === "continente") return !info && d.cont === expCont;
      if (expModo === "banda") return !info || !info.bands || !info.bands[expBanda];
      return false;
    });
  }, [dd, expModo, expCont, expBanda]);

  var textoExport = useMemo(function() {
    var pref = [];
    entidadesExport.forEach(function(d) {
      (PREFIXOS[d.entity] || [d.px]).forEach(function(p) { pref.push(expWild ? p + "*" : p); });
    });
    // Entidades diferentes podem compartilhar prefixo (PJ5/PJ6), entao deduplicamos.
    var unicos = Object.keys(pref.reduce(function(a, p) { a[p] = 1; return a; }, {})).sort();
    return expSep === "virgula" ? unicos.join(",") : unicos.join("\n");
  }, [entidadesExport, expWild, expSep, PREFIXOS]);

  function copiar() {
    try {
      if (navigator.clipboard) navigator.clipboard.writeText(textoExport);
      setCopiado(true);
      setTimeout(function() { setCopiado(false); }, 1800);
    } catch (e) { console.error("[HamExpert] falha ao copiar:", e); }
  }

  function baixar() {
    try {
      var blob = new Blob([textoExport], { type: "text/plain;charset=utf-8" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = "hamexpert-prefixos-" + expModo + (expModo === "banda" ? "-" + expBanda : expModo === "continente" ? "-" + expCont : "") + ".txt";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function() { URL.revokeObjectURL(url); }, 1000);
    } catch (e) { console.error("[HamExpert] falha ao baixar:", e); }
  }

  // Melhor faixa de uma lista, respeitando a amostra minima.
  function melhor(lista, campo) {
    var elegiveis = lista.filter(function(x) { return x.n >= MIN_AMOSTRA; });
    if (!elegiveis.length) return null;
    return elegiveis.reduce(function(m, x) { return x[campo] > m[campo] ? x : m; }, elegiveis[0]);
  }

  var maxHoraRar = Math.max.apply(null, [0].concat(efetividade.horas.map(function(x) { return x.media; })));
  var maxDiaRar = Math.max.apply(null, [0].concat(efetividade.dias.map(function(x) { return x.media; })));
  var maxBandaRar = Math.max.apply(null, [0].concat(efetividade.bandas.map(function(x) { return x.media; })));
  var maxHoraNov = Math.max.apply(null, [0].concat(novidades.horas.map(function(x) { return x.n; })));
  var maxDiaNov = Math.max.apply(null, [0].concat(novidades.dias.map(function(x) { return x.n; })));
  var maxBandaNov = Math.max.apply(null, [0].concat(novidades.bandas.map(function(x) { return x.n; })));

  var melhorHora = melhor(efetividade.horas, "media");
  var melhorDia = melhor(efetividade.dias, "media");
  var melhorBanda = melhor(efetividade.bandas, "media");

  var botao = function(ativo) {
    return { padding: "6px 14px", borderRadius: 6, border: "1px solid " + (ativo ? C.a1 : C.bd), background: ativo ? C.a1 + "20" : C.s2, color: ativo ? C.a1 : C.t2, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Courier New',monospace" };
  };
  var hint = { fontSize: 11, color: C.t3, lineHeight: 1.6 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ═══════ 1. EFETIVIDADE POR RARIDADE ═══════ */}
      <Card>
        <div style={{ fontWeight: 700, color: C.a2, fontSize: 14, fontFamily: "'Courier New',monospace", marginBottom: 4 }}>
          {"\u{1F48E}"} {tr(lang, "Efetividade por raridade", "Effectiveness by rarity")}
        </div>
        <div style={{ ...hint, marginBottom: 14 }}>
          {tr(lang,
            "Indice de raridade 0-100 de cada entidade, medido pela fracao de operadores do HamExpert que ja a alcancou. Aqui ele mostra QUANDO a sua estacao pega o que e dificil - a media do indice por horario, dia e banda. Horarios em UTC.",
            "0-100 rarity index per entity, measured by the share of HamExpert operators that have reached it. Here it shows WHEN your station catches the hard ones - the average index by hour, day and band. Times in UTC.")}
        </div>

        {!rar ? (
          <div style={{ ...hint, padding: "18px 0", textAlign: "center" }}>
            {tr(lang, "Calculando a raridade da base...", "Computing rarity from the database...")}
          </div>
        ) : efetividade.comRaridade === 0 ? (
          <div style={{ ...hint, padding: "18px 0", textAlign: "center" }}>
            {tr(lang, "Nenhum QSO com entidade reconhecida ainda.", "No QSO with a recognised entity yet.")}
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              {[
                { rot: tr(lang, "Melhor horario", "Best hour"), val: melhorHora ? String(melhorHora.chave).padStart(2, "0") + ":00 UTC" : "-", sub: melhorHora ? tr(lang, "indice medio ", "avg index ") + Math.round(melhorHora.media) : tr(lang, "amostra pequena", "small sample") },
                { rot: tr(lang, "Melhor dia", "Best day"), val: melhorDia ? dias[melhorDia.chave] : "-", sub: melhorDia ? tr(lang, "indice medio ", "avg index ") + Math.round(melhorDia.media) : tr(lang, "amostra pequena", "small sample") },
                { rot: tr(lang, "Melhor banda", "Best band"), val: melhorBanda ? melhorBanda.chave : "-", sub: melhorBanda ? tr(lang, "indice medio ", "avg index ") + Math.round(melhorBanda.media) : tr(lang, "amostra pequena", "small sample") }
              ].map(function(k, i) {
                return (
                  <div key={i} style={{ flex: "1 1 150px", background: C.s1, borderRadius: 8, padding: "10px 14px", border: "1px solid " + C.bd }}>
                    <div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: 1 }}>{k.rot}</div>
                    <div style={{ fontSize: 20, color: C.a2, fontWeight: 700, fontFamily: "'Courier New',monospace" }}>{k.val}</div>
                    <div style={{ fontSize: 10, color: C.t3 }}>{k.sub}</div>
                  </div>
                );
              })}
            </div>

            <Secao titulo={tr(lang, "Por horario (UTC)", "By hour (UTC)")}>
              {efetividade.horas.map(function(x) {
                return <Barra key={x.chave} rotulo={String(x.chave).padStart(2, "0") + "h"} valor={x.media} max={maxHoraRar}
                  destaque={melhorHora && x.chave === melhorHora.chave}
                  texto={x.n ? Math.round(x.media) + " · " + x.n + " QSO" : "-"} />;
              })}
            </Secao>

            <Secao titulo={tr(lang, "Por dia da semana", "By day of week")}>
              {efetividade.dias.map(function(x) {
                return <Barra key={x.chave} rotulo={dias[x.chave]} valor={x.media} max={maxDiaRar}
                  destaque={melhorDia && x.chave === melhorDia.chave}
                  texto={x.n ? Math.round(x.media) + " · " + x.n + " QSO" : "-"} />;
              })}
            </Secao>

            <Secao titulo={tr(lang, "Por banda", "By band")}>
              {efetividade.bandas.map(function(x) {
                return <Barra key={x.chave} rotulo={x.chave} valor={x.media} max={maxBandaRar}
                  destaque={melhorBanda && x.chave === melhorBanda.chave}
                  texto={x.n ? Math.round(x.media) + " · " + x.n + " QSO" : "-"} />;
              })}
            </Secao>

            <div style={hint}>
              {tr(lang, "Barras com menos de " + MIN_AMOSTRA + " QSOs aparecem no grafico mas nao concorrem a \"melhor\", para um contato isolado nao virar recomendacao.",
                        "Bars with fewer than " + MIN_AMOSTRA + " QSOs still show but cannot win \"best\", so a single lucky contact never becomes advice.")}
            </div>
          </div>
        )}
      </Card>

      {/* ═══════ 2. QUANDO ENTRAM NOVIDADES ═══════ */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
          <div style={{ fontWeight: 700, color: C.a6, fontSize: 14, fontFamily: "'Courier New',monospace" }}>
            {"\u{1F195}"} {tr(lang, "Quando voce conquista novidades", "When you catch new ones")}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={function() { setNovoTipo("entidade"); }} style={botao(novoTipo === "entidade")}>DXCC</button>
            <button onClick={function() { setNovoTipo("grid"); }} style={botao(novoTipo === "grid")}>GRID</button>
          </div>
        </div>
        <div style={{ ...hint, marginBottom: 14 }}>
          {novoTipo === "entidade"
            ? tr(lang, "Momento em que cada entidade entrou no seu log pela PRIMEIRA vez. Total: " + novidades.total + " entidades.",
                       "When each entity first entered your log. Total: " + novidades.total + " entities.")
            : tr(lang, "Momento em que cada grid de 4 caracteres entrou no seu log pela PRIMEIRA vez. Total: " + novidades.total + " grids.",
                       "When each 4-character grid first entered your log. Total: " + novidades.total + " grids.")}
        </div>

        {novidades.total === 0 ? (
          <div style={{ ...hint, padding: "18px 0", textAlign: "center" }}>{tr(lang, "Sem dados suficientes.", "Not enough data.")}</div>
        ) : (
          <div>
            <Secao titulo={tr(lang, "Por horario (UTC)", "By hour (UTC)")}>
              {novidades.horas.map(function(x) {
                return <Barra key={x.chave} rotulo={String(x.chave).padStart(2, "0") + "h"} valor={x.n} max={maxHoraNov}
                  destaque={x.n > 0 && x.n === maxHoraNov} texto={x.n ? String(x.n) : "-"} />;
              })}
            </Secao>
            <Secao titulo={tr(lang, "Por dia da semana", "By day of week")}>
              {novidades.dias.map(function(x) {
                return <Barra key={x.chave} rotulo={dias[x.chave]} valor={x.n} max={maxDiaNov}
                  destaque={x.n > 0 && x.n === maxDiaNov} texto={x.n ? String(x.n) : "-"} />;
              })}
            </Secao>
            <Secao titulo={tr(lang, "Por banda", "By band")}>
              {novidades.bandas.map(function(x) {
                return <Barra key={x.chave} rotulo={x.chave} valor={x.n} max={maxBandaNov}
                  destaque={x.n > 0 && x.n === maxBandaNov} texto={String(x.n)} />;
              })}
            </Secao>
          </div>
        )}
      </Card>

      {/* ═══════ 3. EXPORTAR PREFIXOS DO QUE FALTA ═══════ */}
      <Card>
        <div style={{ fontWeight: 700, color: C.a3, fontSize: 14, fontFamily: "'Courier New',monospace", marginBottom: 4 }}>
          {"\u{1F4E4}"} {tr(lang, "Exportar prefixos do que falta", "Export prefixes of what you need")}
        </div>
        <div style={{ ...hint, marginBottom: 14 }}>
          {tr(lang, "Gera a lista de prefixos das entidades que voce ainda nao trabalhou, para colar no JTDX, JT Alert e afins.",
                    "Builds the prefix list of the entities you have not worked yet, to paste into JTDX, JT Alert and similar.")}
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          <button onClick={function() { setExpModo("faltantes"); }} style={botao(expModo === "faltantes")}>{tr(lang, "Todas que faltam", "All missing")}</button>
          <button onClick={function() { setExpModo("continente"); }} style={botao(expModo === "continente")}>{tr(lang, "Por continente", "By continent")}</button>
          <button onClick={function() { setExpModo("banda"); }} style={botao(expModo === "banda")}>{tr(lang, "Por banda", "By band")}</button>
        </div>

        {expModo === "continente" && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {CONTINENTES.map(function(c) {
              return <button key={c} onClick={function() { setExpCont(c); }} style={botao(expCont === c)}>{c}</button>;
            })}
          </div>
        )}
        {expModo === "banda" && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {BAND_ORDER.map(function(b) {
              return <button key={b} onClick={function() { setExpBanda(b); }} style={botao(expBanda === b)}>{b}</button>;
            })}
          </div>
        )}

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: C.t3, textTransform: "uppercase", letterSpacing: 1 }}>{tr(lang, "Formato", "Format")}:</span>
          <button onClick={function() { setExpSep("virgula"); }} style={botao(expSep === "virgula")}>{tr(lang, "virgula", "comma")}</button>
          <button onClick={function() { setExpSep("linha"); }} style={botao(expSep === "linha")}>{tr(lang, "uma por linha", "one per line")}</button>
          <button onClick={function() { setExpWild(!expWild); }} style={botao(expWild)}>{tr(lang, "com curinga *", "with wildcard *")}</button>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: C.t2, fontFamily: "'Courier New',monospace" }}>
            {entidadesExport.length} {tr(lang, "entidades", "entities")} {"·"} {textoExport ? textoExport.split(expSep === "virgula" ? "," : "\n").length : 0} {tr(lang, "prefixos", "prefixes")}
          </div>
          <button onClick={copiar} style={botao(false)}>{copiado ? tr(lang, "copiado!", "copied!") : tr(lang, "copiar", "copy")}</button>
          <button onClick={baixar} style={botao(false)}>{tr(lang, "baixar .txt", "download .txt")}</button>
        </div>

        <textarea readOnly value={textoExport} spellCheck={false}
          style={{ width: "100%", minHeight: 130, background: C.s1, border: "1px solid " + C.bd, borderRadius: 8, color: C.t2, fontSize: 12, fontFamily: "'Courier New',monospace", padding: 10, resize: "vertical", boxSizing: "border-box" }} />

        <div style={{ ...hint, marginTop: 8 }}>
          {expModo === "banda"
            ? tr(lang, "No modo por banda entram tambem as entidades que voce ja trabalhou, mas nao nesta banda.",
                       "In band mode this also includes entities you have worked, just not on this band.")
            : tr(lang, "Cada entidade traz o prefixo principal e todos os aliases conhecidos (PY tambem gera PP, PQ, PU, ZZ...).",
                       "Each entity brings its main prefix plus every known alias (PY also yields PP, PQ, PU, ZZ...).")}
        </div>
      </Card>
    </div>
  );
}
